import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface HeroIntroProps {
  onComplete: () => void;
  canSkip?: boolean;
}

const HeroIntro: React.FC<HeroIntroProps> = ({ onComplete, canSkip = true }) => {
  const [showSkip, setShowSkip] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const prefersReduced = useReducedMotion();
  const lowMemory = useMemo(() => {
    try {
      const dm = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
      return typeof dm === 'number' && dm <= 4;
    } catch (_) {
      return false;
    }
  }, []);
  const enableMotion = !prefersReduced && !lowMemory;
  const viewportH = useMemo(() => (typeof window !== 'undefined' ? window.innerHeight : 800), []);
  const logoUrl = '/images/logo-abask.svg';

  // Brand palette tuned from logo
  const brandBlue = '#283C74';
  const brandBlueDark = '#243C74';
  const brandNavy = '#0A132A';
  const brandGold = '#D9B86E';

  // CSS mask style to clip effects to the exact logo silhouette
  const logoMaskStyle: React.CSSProperties = {
    WebkitMaskImage: `url(${logoUrl})`,
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskImage: `url(${logoUrl})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain',
  };

  useEffect(() => {
    // Auto-complete after 3 seconds (reduced from 5)
    const autoCompleteTimer = setTimeout(() => {
      setIsComplete(true);
      setTimeout(onComplete, 600); // Wait for exit animation
    }, 3000);

    // Show skip button after 1.5 seconds
    const skipTimer = setTimeout(() => setShowSkip(true), 1500);

    return () => {
      clearTimeout(skipTimer);
      clearTimeout(autoCompleteTimer);
    };
  }, [onComplete]);

  const handleSkip = () => {
    if (!canSkip) return;
    setIsComplete(true);
    setTimeout(onComplete, 300);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
      handleSkip();
    }
  };

  return (
    <AnimatePresence>
      {!isComplete && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          onKeyDown={handleKeyPress}
          tabIndex={0}
          role="dialog"
          aria-label="مقدمة الموقع - اضغط أي مفتاح للتخطي"
        >
          {/* Cinematic Background Layers */}
          <div className="absolute inset-0 -z-10" aria-hidden>
            {/* Deep luxe gradient base matched to logo blues */}
            <div className="absolute inset-0" style={{
              background:
                `radial-gradient(120% 90% at 50% 42%, ${brandBlueDark} 0%, #152447 38%, #0E1C3B 70%, ${brandNavy} 100%)`
            }} />
            {/* Soft center aura under logo to lift contrast */}
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(40% 30% at 50% 48%, rgba(40,60,116,0.35) 0%, rgba(40,60,116,0.15) 35%, rgba(40,60,116,0) 70%)',
              filter: 'blur(8px)'
            }} />
            {/* Vignette for focus without mid-line artifacts */}
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(115% 90% at 50% 45%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.38) 100%)'
            }} />
            {/* Gentle vertical fade top/bottom (reduced intensity to avoid banding) */}
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/30 via-black/5 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/35 via-black/5 to-transparent" />

            {/* Gold shimmer sweep over background (now gently moving across X) */}
            <motion.div
              className="absolute -left-[20%] top-1/3 w-[140%] h-px -rotate-6"
              style={{
                background:
                  `linear-gradient(90deg, transparent 0%, rgba(217,184,110,0.00) 10%, rgba(217,184,110,0.5) 50%, rgba(217,184,110,0.00) 90%, transparent 100%)`,
                filter: 'drop-shadow(0 0 14px rgba(217,184,110,0.35))',
                opacity: 0.35,
              }}
              initial={enableMotion ? { x: '-6%' } : undefined}
              animate={enableMotion ? { x: ['-6%', '6%', '-6%'] } : undefined}
              transition={enableMotion ? { duration: 7, repeat: Infinity, ease: 'easeInOut' } : undefined}
            />
            {enableMotion && (
              <motion.div
                className="absolute -left-[18%] top-1/2 w-[136%] h-[2px] -rotate-3"
                style={{
                  background:
                    `linear-gradient(90deg, transparent 0%, rgba(217,184,110,0.00) 15%, rgba(217,184,110,0.35) 50%, rgba(217,184,110,0.00) 85%, transparent 100%)`,
                  filter: 'blur(0.8px)',
                  opacity: 0.25,
                  mixBlendMode: 'screen'
                }}
                initial={{ x: '8%' }}
                animate={{ x: ['8%','-8%','8%'] }}
                transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
              />
            )}

            {/* Floating dust particles (very subtle) */}
            {enableMotion && (
              <div className="absolute inset-0 overflow-hidden">
                {[...Array(10)].map((_, i) => {
                  const delay = (i % 5) * 0.6 + Math.random() * 0.5;
                  const duration = 7 + (i % 4) * 2 + Math.random() * 2;
                  const startX = Math.random() * 100; // vw
                  const size = 1 + (i % 2);
                  const opacity = 0.05 + (i % 3) * 0.04;
                  return (
                    <motion.span
                      key={i}
                      className="absolute rounded-full bg-white"
                      style={{
                        left: `${startX}vw`,
                        bottom: '-24px',
                        width: `${size}px`,
                        height: `${size}px`,
                        opacity,
                        filter: 'blur(0.4px)'
                      }}
                      initial={{ y: 0, opacity: 0 }}
                      animate={{ y: -viewportH - 40, opacity: [0, opacity, 0] }}
                      transition={{ duration, delay, repeat: Infinity, ease: 'linear' }}
                    />
                  );
                })}
              </div>
            )}
          </div>
          {/* Skip Button */}
          <AnimatePresence>
            {showSkip && canSkip && (
              <motion.button
                onClick={handleSkip}
                className={cn(
                  'absolute top-6 right-6 z-10',
                  'px-4 py-2 text-white/70 hover:text-white',
                  'border border-white/30 hover:border-white/50 rounded-lg',
                  'transition-colors duration-200',
                  'text-sm backdrop-blur-sm bg-white/5',
                  'focus:outline-none focus:ring-2 focus:ring-white/50'
                )}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                aria-label="تخطي المقدمة"
              >
                تخطي
              </motion.button>
            )}
          </AnimatePresence>

          {/* Logo Animation */}
          <motion.div
              className="relative"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ 
              scale: [0.92, 1.06, 1], 
              opacity: [0, 0.9, 1] 
            }}
            transition={{ 
              duration: 1.8,
              times: [0, 0.55, 1],
              ease: "easeOut"
            }}
          >
            {/* Clean ABASK Logo from Supabase */}
            <motion.div 
              className="relative w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={enableMotion ? { 
                scale: [0.9, 1.065, 1, 1.01, 1], 
                opacity: [0, 0.9, 1, 1, 1] 
              } : { scale: 1, opacity: 1 }}
              transition={enableMotion ? { 
                duration: 2.2,
                times: [0, 0.45, 0.9, 0.97, 1],
                ease: "easeOut"
              } : undefined}
            >
              {/* Masked Glow confined to logo shape (removes circular aura) */}
              <div className="absolute inset-0" style={logoMaskStyle} aria-hidden>
                {/* Inner gold fill with soft pulsation */}
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: `radial-gradient(52% 62% at 50% 45%, rgba(217,184,110,0.5) 0%, rgba(217,184,110,0.28) 42%, rgba(217,184,110,0.12) 70%, rgba(217,184,110,0.00) 100%)`,
                    filter: 'blur(8px)'
                  }}
                  initial={{ opacity: 0.0 }}
                  animate={enableMotion ? { opacity: [0.18, 0.34, 0.22] } : { opacity: 0.26 }}
                  transition={enableMotion ? { duration: 3.0, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' } : undefined}
                />
                {/* Outer faint halo still clipped to silhouette for readability */}
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: `radial-gradient(65% 70% at 50% 50%, rgba(217,184,110,0.18) 0%, rgba(217,184,110,0.10) 45%, rgba(217,184,110,0.00) 85%)`,
                    filter: 'blur(14px)'
                  }}
                  initial={{ opacity: 0.0 }}
                  animate={enableMotion ? { opacity: [0.08, 0.18, 0.1] } : { opacity: 0.12 }}
                  transition={enableMotion ? { duration: 3.8, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' } : undefined}
                />

                {/* Stronger diagonal sweep over the logo */}
                {enableMotion && (
                  <motion.div
                    className="absolute -inset-2"
                    style={{
                      background: 'linear-gradient(120deg, rgba(255,255,255,0) 35%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 65%)',
                      mixBlendMode: 'screen',
                      filter: 'blur(2.2px)'
                    }}
                    initial={{ x: '-130%', rotate: -18 }}
                    animate={{ x: '130%' }}
                    transition={{ duration: 1.35, ease: 'easeInOut', repeat: Infinity, repeatDelay: 1.1 }}
                  />
                )}

                {/* Second delayed shimmer pass for richness */}
                {enableMotion && (
                  <motion.div
                    className="absolute -inset-3"
                    style={{
                      background: 'linear-gradient(120deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 70%)',
                      mixBlendMode: 'screen',
                      filter: 'blur(2px)'
                    }}
                    initial={{ x: '130%', rotate: -18 }}
                    animate={{ x: '-130%' }}
                    transition={{ duration: 1.9, ease: 'easeInOut', repeat: Infinity, repeatDelay: 1.2 }}
                  />
                )}

                {/* Edge sparkles near silhouette perimeter */}
                {enableMotion && (
                  <div className="absolute inset-0">
                    {Array.from({ length: 18 }).map((_, i) => {
                      const base = i * 41.7; // spread points more
                      const rad = (base * Math.PI) / 180;
                      const r = 42 + (i % 3) * 2.8; // radius in % of container
                      const jitter = ((i % 2 ? 1 : -1) * (1.1 + (i % 3) * 0.45));
                      const left = `calc(50% + ${(Math.cos(rad) * r).toFixed(2)}%)`;
                      const top = `calc(50% + ${(Math.sin(rad) * r).toFixed(2)}%)`;
                      return (
                        <motion.span
                          key={i}
                          className="absolute rounded-full"
                          style={{
                            left,
                            top,
                            width: '2.6px',
                            height: '2.6px',
                            backgroundColor: 'rgba(217,184,110,0.98)',
                            boxShadow: '0 0 12px rgba(217,184,110,0.9), 0 0 18px rgba(217,184,110,0.5)'
                          }}
                          initial={{ opacity: 0.0, x: 0, y: 0, scale: 0.9 }}
                          animate={{ opacity: [0.0, 1, 0.0], x: [0, jitter, 0], y: [0, -jitter, 0], scale: [0.9, 1.05, 0.95] }}
                          transition={{ duration: 2.1 + (i % 5) * 0.28, delay: (i % 7) * 0.16, repeat: Infinity, ease: 'easeInOut' }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Logo Image with Animations */}
              <motion.img
                src={logoUrl}
                alt="شعار أباسك"
                className="w-full h-full object-contain relative z-10"
                style={{
                  filter: 'drop-shadow(0 0 36px rgba(217, 184, 110, 0.55)) saturate(1.06) contrast(1.05)'
                }}
                initial={{ rotate: -6, filter: 'drop-shadow(0 0 0px rgba(217, 184, 110, 0)) saturate(1) contrast(1)' }}
                animate={{ rotate: 0, filter: ['drop-shadow(0 0 0px rgba(217, 184, 110, 0)) saturate(1) contrast(1)','drop-shadow(0 0 48px rgba(217, 184, 110, 0.85)) saturate(1.08) contrast(1.06)','drop-shadow(0 0 28px rgba(217, 184, 110, 0.55)) saturate(1.06) contrast(1.05)'] }}
                transition={{ duration: 2.2, times: [0, 0.68, 1], ease: "easeOut" }}
                onError={(e) => {
                  // Fallback to local logo if URL fails
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/logo-abask.svg';
                }}
              />

              {/* Light sweep over logo */}
              {enableMotion && (
                <motion.div
                  className="pointer-events-none absolute inset-0 overflow-hidden"
                  style={logoMaskStyle}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0.65, 1] }}
                  transition={{ duration: 2.8, repeat: Infinity, repeatDelay: 1.8 }}
                >
                  <motion.div
                    className="absolute -inset-1"
                    style={{
                      background: 'linear-gradient(120deg, rgba(255,255,255,0) 28%, rgba(255,255,255,0.55) 45%, rgba(255,255,255,0) 62%)',
                      mixBlendMode: 'screen',
                      filter: 'blur(1.8px)'
                    }}
                    initial={{ x: '-120%', rotate: -10 }}
                    animate={{ x: '120%' }}
                    transition={{ duration: 1.5, ease: 'easeInOut' }}
                  />
                </motion.div>
              )}

              {/* Removed previous orbit sparks to avoid visual clutter; edge sparkles are masked above */}
            </motion.div>

            {/* Text Fade-in */}
            <motion.div
              className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3, duration: 1 }}
            >
              <span className="text-white/60 text-sm tracking-wide">أباسك</span>
            </motion.div>
          </motion.div>

          {/* Click/Tap anywhere to skip */}
          <motion.div
            className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
          >
            <p className="text-white/50 text-xs">اضغط في أي مكان للتخطي</p>
          </motion.div>

          {/* Allow click anywhere to skip when enabled */}
          {canSkip && (
            <button
              aria-label="تخطي المقدمة"
              className="absolute inset-0"
              onClick={handleSkip}
              style={{ cursor: 'default' }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HeroIntro;